import React from 'react';
import * as messages from '@radnuc/messages';

import * as query from './Query';
import { uuidToString } from './UUID';
import { Config } from './Config';

import { withTranslation } from 'react-i18next';
import { t } from 'i18next';
import './i18n';

import './ContextDisplay.css'

import day_logo from './img/sun-day-bright-light-48_white.png'
import night_logo from './img/weather-night-48_white.png'
import icon_attention_logo from './img/icon-attention.png'
import icon_industrial_logo from './img/icon-industrial.png'
import icon_medical_logo from './img/icon-medical.png'
import icon_nuclear_logo from './img/icon-nuclear.png'
import icon_norm_logo from './img/icon-norm.png'
import icon_switch_video_logo from './img/ic_switch_video_48px_white.png'

type Investigation = messages.Encounter.Encounter | messages.Rad.Investigation;
//export type RelativeBearingToLane = {[bearing:number]:number};
//export type LaneRelativeBearing = {[lane:number]:{bearing:number,pos:number}};
export type LanePosition = {[position:number]:number};

interface ContextAttributionListProps {
    analysis?: messages.Contextual.ContextImageAnalysis[],
    track2d?: messages.Contextual.ContextTrack2DAnalysis[],
    track3d?: messages.Contextual.ContextTrack3DAnalysis[],
    attribution?: messages.Contextual.SourceAttributionAnalysis,

    selectedTrack?: {[key:string]:number},
    onSelectTrack: (track: string, rank: number) => void,

    replayInvestigation?: Investigation,
}

class ContextAttributionList extends React.Component<ContextAttributionListProps> {

    checkAttributionError(){
        let result = false;
        if(this.props.replayInvestigation){
            if('type' in this.props.replayInvestigation){
                if(this.props.replayInvestigation.type === messages.Encounter.EncounterType.RAD){
                    if(this.props.attribution === undefined){
                           result = true;
                    }
                }
            } else {
                //Investigation message = RAD only
                if(this.props.attribution === undefined){
                       result = true;
                }
            }
        }
        return result;
    }

    render() {
        if (this.props.analysis === undefined || this.props.track2d === undefined || this.props.track3d === undefined)
            return <></>;

        let objects: {[key: string]: messages.Contextual.ContextObject} = {}
        this.props.analysis?.forEach((analysis) => {
            analysis.objects.forEach((object) => {objects[uuidToString(object.id)] = object})
        });

        let track2ds: {[key: string]: messages.Contextual.ContextTrack2D[]} = {}
        this.props.track2d?.forEach((track2dAnalysis) =>
            track2dAnalysis.tracks.forEach((track2d) => {
                const key = uuidToString(track2d.id);
                if (!(key in track2ds))
                    track2ds[key] = [];
                track2ds[key].push(track2d)
            }));

        let track3ds: {[key: string]: string} = {}
        this.props.track3d?.forEach((track3dAnalysis) =>
            track3dAnalysis.tracks.forEach((track3d) => {
                const key = uuidToString(track3d.id);
                if (track3d.assignments.length === 0 || !(uuidToString(track3d.assignments[0].track2D) in track2ds))
                    return

                track3ds[key] = uuidToString(track3d.assignments[0].track2D);
            }));

        let attributions: {[key: string]: number} = {}
        this.props.attribution?.attribution?.forEach((attr) => {
            attributions[uuidToString(attr.track3D)] = attr.likelihood;
        })
        let attributionErrorFlag = this.checkAttributionError();

        const maxAttribution = Math.max(...Object.values(attributions));

        const track3dData = Object.keys(track3ds).map((track3d) => {
            const t2s = track2ds[track3ds[track3d]];
            if (t2s === undefined)
                return {key: track3d, type: 'Unknown', duration: 0,
                    rawConfidence: -1e100, confidence: 'N/A'};

            const times = t2s.map((t2) => t2.time);
            const duration = Math.max(...times) - Math.min(...times);

            let type: string = "Unknown";
            let count = 0;

            t2s.forEach((t2) => {
                if (t2.assignments.length === 0)
                    return;

                const object = objects[uuidToString(t2.assignments[0].object)]
                if (object === undefined)
                    return;

                if (object.type === type) {
                    count += 1;
                } else {
                    if (count === 0) {
                        type = object.type;
                        count = 1;
                    } else {
                        count -= 1;
                    }
                }
            });

            let confidence = 'N/A'; //If the track is NOT in attribution, set to N/A
            let rawConfidence = -2e100;
            if(track3d in attributions){
                rawConfidence = attributions[track3d];
                confidence = 'NONE'; //N.B. If the track is in attribution, set it to lowest confidence = NONE
                if (maxAttribution > 0) {
                    if (attributions[track3d] > 4 * maxAttribution / 5)
                        confidence = 'HIGH'
                    else if (attributions[track3d] > maxAttribution / 3)
                        confidence = 'MED'
                    else if (attributions[track3d] <= -1e100)
                        confidence = 'NONE'
                    else
                        confidence = 'LOW'
                }
            }
            //confidence = rawConfidence.toString(); //HACK TO DISPLAY RAW CONF VALUE
            if(attributionErrorFlag){
                confidence = 'ERROR';
            }

            return {key: track3d, type: type, duration: duration,
                rawConfidence: rawConfidence, confidence: confidence};
        }).sort((a, b) => {return -a.rawConfidence + b.rawConfidence})
        .filter((val) => val.duration > 300)
        .filter((val) => val.rawConfidence > -2e100);


        const tracks = track3dData.map((data, idx) => {
            //if(data.confidence !== 'UNK')
            let selected_flag = false;
            if(this.props.selectedTrack !== undefined){
                selected_flag = Object.keys(this.props.selectedTrack).includes(data.key);
            }
            return <div key={data.key} className={`ContextObject ${data.confidence} ${selected_flag? 'selected': ''}`}
                        style={{display: 'flex', flexDirection: 'row'}}
                        onClick={(event) => {this.props.onSelectTrack(data.key,idx+1)}}>
                    <span style={{width: '20px'}} className="index">{idx+1}</span>
                    <span style={{flex: 1}} className="type">{t(data.type.trim(),{ns:'trans'})}</span>
                    <span style={{flex: 1}} className="duration">{Math.round(data.duration / 1000)}{t('s',{ns:'trans'})}</span>
                    <span style={{flex: 1}} className="confidence">{t(data.confidence,{ns:'trans'})}</span>
                </div>;
        });


        return <div className="ContextAttributionList" style={{ flex: 1 }}>
                <div className="title" style={{display: 'flex', flexDirection: 'row'}}>
                    <span style={{width: '20px'}} className="index"></span>
                    <span style={{flex: 1}} className="type">{t('Type',{ns:'trans'})}</span>
                    <span style={{flex: 1}} className="duration">{t('Duration',{ns:'trans'})}</span>
                    <span style={{flex: 1}} className="confidence">{t('Confidence',{ns:'trans'})}</span>
                </div>
                {this.checkAttributionError() ? <div style={{textAlign: 'center', color:'red'}}>{t('ATTRIBUTION ERROR',{ns:'trans'})}</div> : tracks}
            </div>;
    }
} withTranslation()


export type CoralResults = {[key:string]:messages.Contextual.CoralResult}
export interface CoralInterface {
    coralResults?:CoralResults,
    selectedSource?: {[key:string]:number},
    replayInvestigation?: Investigation,
    updateSelectedSource: (selectedSource:{[key:string]:number}) => void,
    updateSelectedInvestigation: (inv: Investigation) => void,
}

export interface CoralResultListProps {
    selectedSource?: {[key:string]:number},
    onSelectSource: (source: string, timestamp: number) => void,

    replayInvestigation?: Investigation,
    coralResults?: CoralResults,
}

class CoralResultList extends React.Component<CoralResultListProps> {
    render() {
        let resultList;
        if(this.props.coralResults !== undefined && Object.keys(this.props.coralResults).length > 0){
            const thisCoralResult = this.props.coralResults[uuidToString(this.props.replayInvestigation?.id)];
            if(thisCoralResult !== undefined){
                const thisCoralResultSources = thisCoralResult.sources.sort((a, b) => {return a.rank - b.rank});
                resultList = thisCoralResultSources.map((data, idx) => {
                    let selected_flag = false;
                    if(this.props.selectedSource !== undefined){
                        selected_flag = Object.keys(this.props.selectedSource).includes(data.name);
                    }

                    return <div key={data.name} className={`CoralObject ${data.likelihood} ${selected_flag? 'selected': ''}`}
                                style={{display: 'flex', flexDirection: 'row'}}
                                onClick={(event) => {this.props.onSelectSource(data.name,data.image.time)}}>
                            <span style={{width: '20px'}} className="index">{data.rank}</span>
                            <span style={{flex: 1, flexBasis:'80%',paddingRight:'10px'}} className="name">{t(data.name.trim(),{ns:'trans'})}</span>
                            <span style={{flex: 1, flexBasis:'20%'}} className="confidence">{t(data.likelihood,{ns:'trans'})}</span>
                        </div>;
                });
            }
        }

        return <div className="CoralResultList" style={{ flex: 1 }}>
                <div className="title" style={{display: 'flex', flexDirection: 'row'}}>
                    <span style={{width: '20px'}} className="index"></span>
                    <span style={{flex: 1, flexBasis:'80%'}} className="name">{t('Name',{ns:'trans'})}</span>
                    <span style={{flex: 1, flexBasis:'20%'}} className="confidence">{t('Confidence',{ns:'trans'})}</span>
                </div>
                {resultList}

            </div>;
    }
}

export interface CoralImageDisplayProps {
    contextImage?: messages.Contextual.ContextImage
}

export class CoralImageDisplay extends React.Component<CoralImageDisplayProps> {
    canvasRef: React.RefObject<HTMLCanvasElement> = React.createRef<HTMLCanvasElement>();
    canvasContainerRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
    animationFrame: number | null = null;
    constructor(props: CoralImageDisplayProps) {
        super(props)
        this.resize = this.resize.bind(this);
    }
    componentDidMount() {
        window.addEventListener("resize", this.resize);
        this.drawLoadingFrame();
        if(this.props.contextImage !== undefined){
            this.drawImage(this.props.contextImage);
        }
    }

    componentDidUpdate(prevProps: CoralImageDisplayProps) {
        if(this.props.contextImage !== undefined){
            if(prevProps.contextImage !== undefined){
                if(this.props.contextImage.image_data !== prevProps.contextImage.image_data){
                    this.drawImage(this.props.contextImage);
                }
            } else {
                this.drawImage(this.props.contextImage);
            }
        }
    }
    resize(){
        if (this.canvasRef.current !== null){
            this.canvasRef.current.width = 0;
            this.canvasRef.current.height = 0;
        }

        this.drawLoadingFrame();
        if(this.props.contextImage !== undefined)
            this.drawImage(this.props.contextImage);
    }

    clearFrame(){
        const canvas = this.canvasRef.current;
        const container = this.canvasContainerRef.current;
        if(canvas === null || container === null)
            return
        if (canvas.width !== container.clientWidth)
            canvas.width = container.clientWidth;
        const context = canvas.getContext('2d');
        if (context === null)
            return;
        context.fillStyle = 'rgb(16,16,16)';
        context.globalAlpha = 1;
        context.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    }

    drawImage(contextImage:messages.Contextual.ContextImage) {

        if (contextImage === undefined){
            this.clearFrame();
            return;
        }

        if (this.canvasRef.current == null || this.canvasContainerRef.current === null)
            return;

        //if (this.replayLoadingTimeout !== undefined)
        //    clearTimeout(this.replayLoadingTimeout);

        //this.replayLoadingTimeout = undefined;

        if (this.animationFrame !== null)
            window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;

        //this.currentReplayImage = imageRef;

        const canvas = this.canvasRef.current;
        const container = this.canvasContainerRef.current;
        if (canvas.width !== container.clientWidth)
            canvas.width = container.clientWidth;

        let image = new Image()
        let imageData = contextImage.image_data as unknown as Uint8Array;

        var blob = new Blob([imageData], { type: "image/jpeg" });
        var imageUrl = URL.createObjectURL(blob);
        image.src = imageUrl;

        image.onload = () => {
            const height = Math.floor(image.height * canvas.width / image.width);
            container.style.height = `${height}px`;
            if (canvas.height !== height)
                canvas.height = height;

            const context = canvas.getContext('2d');
            if (context === null)
                return;

            context?.drawImage(image, 0, 0, canvas.width, canvas.height);
        }
        /*
        this.replayQueryActive = false;
        if (this.currentReplayImage !== this.nextReplayImage) {
            this.drawReplayFrame(this.nextReplayImage);
        }
        */

    }
    drawLoadingFrame() {
        if (this.canvasRef.current == null || this.canvasContainerRef.current === null)
            return;

        if (this.animationFrame !== null)
            window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;

        const canvas = this.canvasRef.current;
        const container = this.canvasContainerRef.current;
        if (canvas.width !== container.clientWidth)
            canvas.width = container.clientWidth;

        const height = Math.floor(3 * canvas.width / 4);
        container.style.height = `${height}px`;
        if (canvas.height !== height)
            canvas.height = height;

        const context = canvas.getContext('2d');
        if (context === null) return;

        context.fillStyle = 'grey';
        context.fillRect(0, 0, canvas.width, canvas.height);

        const angle = (new Date().getTime() / 3000 * 2 * Math.PI) % (2 * Math.PI);
        context.strokeStyle = 'black';
        context.lineWidth = 3;

        context.beginPath();
        context.arc(canvas.width / 2, canvas.height / 2,
            canvas.height / 3, angle, angle + 3 * Math.PI / 2)
        context.stroke();
        this.animationFrame = window.requestAnimationFrame(() => this.drawLoadingFrame());
    }

    render(){

        return(
        <div ref={this.canvasContainerRef} style={{ width: '100%'}}>
            <canvas id="coralImage" ref={this.canvasRef} />
        </div>
        )
    }



}

export interface ContextDisplayInterface {
    updateLane: (lane: number) => void,
}


export interface ContextDisplayProps {
    liveStream: boolean,
    remoteDevice: string,

    multipleCamera?: boolean,
    initLane?: number,

    replayTime?: Date,
    //attribution?: messages.Contextual.SourceAttributionSummary,
    attributionAnalysis?: messages.Contextual.SourceAttributionAnalysis,

    images?: Map<number,messages.Contextual.ContextImageReference[]>,
    imageAnalyses?: Map<number,messages.Contextual.ContextImageAnalysis[]>,
    track2d?: Map<number,messages.Contextual.ContextTrack2DAnalysis[]>,
    track3d?: Map<number,messages.Contextual.ContextTrack3DAnalysis[]>,

    investigations?: Investigation[],
    replayInvestigation?: Investigation,
    coralInterface?: CoralInterface,

    lanePositionLUT?:LanePosition,
    showContextAttribution?: boolean,
    hideCameraSelector?: boolean,
    showLaneSelector?: boolean,
    contextDisplayInterface?: ContextDisplayInterface,

    config?: Config,
}

export interface ContextDisplayState {
    connected: boolean,
    thermalCamera: boolean,
    selectedTrack?: {[key:string]:number},
    lane: number,
}

export class ContextDisplay extends React.Component<ContextDisplayProps, ContextDisplayState> {
    canvasRef: React.RefObject<HTMLCanvasElement> = React.createRef<HTMLCanvasElement>();
    canvasContainerRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

    video: HTMLVideoElement = document.createElement('video');
    animationFrame: number | null = null;

    shutdown: boolean = false;
    peerConnection: RTCPeerConnection | null = null;
    restartIceFlag: boolean = false;
    reconnectTimer: NodeJS.Timeout | null = null;

    replayQueryActive: boolean = false;
    replayLoadingTimeout?: NodeJS.Timeout;
    currentReplayImage?: messages.Contextual.ContextImageReference;
    nextReplayImage?: messages.Contextual.ContextImageReference;
    lanePositionLUT:LanePosition = {0:0,1:1};
    turnBorderRedOnLiveDetection: boolean = true;

    showTopNTracks: number = 3;
    defaultTracksShown: boolean = false;

    constructor(props: ContextDisplayProps) {
        super(props)
        //console.log('Instantiate ContextDisplay')
        if(this.props.lanePositionLUT !== undefined)
            this.lanePositionLUT = this.props.lanePositionLUT;

        this.state = { connected: false, thermalCamera: false,
            lane: this.props.multipleCamera ? (this.props.initLane ? this.lanePositionLUT[this.props.initLane] : this.lanePositionLUT[0]) : 0,
        };

        this.resize = this.resize.bind(this);
        this.cleanupBeforeShutdown = this.cleanupBeforeShutdown.bind(this);
    }

    async localConnect(peerConnection: RTCPeerConnection) {
        let port = window.location.port;
        if (window.location.port === '3000')
            port = '8301';
        return await fetch('http://' + window.location.hostname + ':'+port+'/TGW1hNlSwjaNFF96k4QQ9RXFNeiEqRab/video', {
            body: JSON.stringify({
                sdp: peerConnection.localDescription!.sdp,
                type: peerConnection.localDescription!.type,
                camera: this.state.thermalCamera ? 'THERMAL' : 'VISIBLE',
                lane: this.lanePositionLUT[this.state.lane]
            }
            ),
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'POST'
        });
    }

    async remoteConnect(peerConnection: RTCPeerConnection) {
        return await fetch('http://purdas.psicorp.com:8301/TGW1hNlSwjaNFF96k4QQ9RXFNeiEqRab/forward', {
            body: JSON.stringify({
                name: this.props.remoteDevice,
                payload: {
                    sdp: peerConnection.localDescription!.sdp,
                    type: peerConnection.localDescription!.type,
                    camera: this.state.thermalCamera ? 'THERMAL' : 'VISIBLE',
                    lane: this.lanePositionLUT[this.state.lane]
                }
            }),
            headers: {
                'Content-Type': 'application/json'
            },
            method: 'POST'
        });
    }

    handleClose() {
        console.log('Context Display disconnected.');
        if (this.peerConnection !== null) {
            if(!this.video.paused){
                this.video.pause();
            }
            this.peerConnection.getReceivers().forEach((r) => r.track.stop());
            this.video.srcObject = null;
            this.peerConnection.close();
        }
        this.peerConnection = null;

        if (this.animationFrame !== null)
            window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;

        this.setState({ connected: false });

        // Restart WebRTC if we're still connecting
        if (this.props.liveStream && !this.shutdown) {
            //console.log('ContextDisplay: restart WebRTC from handleClose()')
            this.drawLoadingFrame();
            if(this.reconnectTimer !== null){
                clearTimeout(this.reconnectTimer)
            }
            this.reconnectTimer = setTimeout(() => this.connectVideo(false), 1000)
        }
    }

    drawLiveFrame() {
        if (this.video.paused || this.canvasRef.current == null || this.canvasContainerRef.current === null)
            return;

        if (this.animationFrame !== null)
            window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;

        let canvas = this.canvasRef.current;
        const container = this.canvasContainerRef.current;
        if (canvas.width !== container.clientWidth)
            canvas.width = container.clientWidth;

        const height = Math.floor(this.video.videoHeight * canvas.width / this.video.videoWidth);
        container.style.height = `${height}px`;
        if (canvas.height !== height)
            canvas.height = height;

        const context = canvas.getContext('2d');
        context?.drawImage(this.video, 0, 0, canvas.width, canvas.height);
        this.animationFrame = window.requestAnimationFrame(() => this.drawLiveFrame());
    }

    drawLoadingFrame() {
        if (this.canvasRef.current == null || this.canvasContainerRef.current === null)
            return;

        if (this.animationFrame !== null)
            window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;

        const canvas = this.canvasRef.current;
        const container = this.canvasContainerRef.current;
        if (canvas.width !== container.clientWidth)
            canvas.width = container.clientWidth;

        const height = Math.floor(3 * canvas.width / 4);
        container.style.height = `${height}px`;
        if (canvas.height !== height)
            canvas.height = height;

        const context = canvas.getContext('2d');
        if (context === null) return;

        context.fillStyle = 'grey';
        context.fillRect(0, 0, canvas.width, canvas.height);

        const angle = (new Date().getTime() / 3000 * 2 * Math.PI) % (2 * Math.PI);
        context.strokeStyle = 'black';
        context.lineWidth = 3;

        context.beginPath();
        context.arc(canvas.width / 2, canvas.height / 2,
            canvas.height / 3, angle, angle + 3 * Math.PI / 2)
        context.stroke();
        this.animationFrame = window.requestAnimationFrame(() => this.drawLoadingFrame());
    }

    drawBoundingBox(context: CanvasRenderingContext2D, rect: messages.Contextual.ImageRect, scale: number, boxText?: string) {
        let left = rect.left * scale;
        let up = rect.up * scale;
        let right = rect.right * scale;
        let down = rect.down * scale;

        context.beginPath();
        context.lineWidth = 4;
        context.strokeStyle = "blue";
        context.rect(left, up, (right - left), (down - up));
        context.stroke();

        if (boxText !== undefined){
            let text_left = rect.left * scale;
            let text_up = (rect.up-20) * scale;
            context.font = "bold 18px Arial";
            context.fillStyle = "blue";
            context.fillText(boxText,text_left,text_up)
        }
    }

    drawReplayFrame(imageRef?: messages.Contextual.ContextImageReference) {
        if (imageRef === undefined){
            this.clearFrame();
            return;
        }


        // Make sure we always reset next replay image, even if we process this now
        this.nextReplayImage = imageRef;

        if (this.replayQueryActive) {
            return
        }

        this.replayQueryActive = true;
        if (this.replayLoadingTimeout === undefined)
            this.replayLoadingTimeout = setTimeout(() => this.drawLoadingFrame(), 2000); //N.B. Time to display loading indicator

        query.queryImageData(imageRef,10000,this.lanePositionLUT[this.state.lane]).then(result => {
            if (this.canvasRef.current == null || this.canvasContainerRef.current === null)
                return;

            if (this.replayLoadingTimeout !== undefined)
                clearTimeout(this.replayLoadingTimeout);
            this.replayLoadingTimeout = undefined;

            if (this.animationFrame !== null)
                window.cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;

            this.currentReplayImage = imageRef;

            const canvas = this.canvasRef.current;
            const container = this.canvasContainerRef.current;
            if (canvas.width !== container.clientWidth)
                canvas.width = container.clientWidth;

            let image = new Image()
            let imageData = result.image_data as unknown as Uint8Array;

            //let imageDataString = Buffer.from(result.image_data).toString('base64');

            var blob = new Blob([imageData], { type: "image/jpeg" });
            var imageUrl = URL.createObjectURL(blob);
            image.src = imageUrl;

            image.onload = () => {
                const height = Math.floor(image.height * canvas.width / image.width);
                container.style.height = `${height}px`;
                if (canvas.height !== height)
                    canvas.height = height;

                const context = canvas.getContext('2d');
                if (context === null)
                    return;

                context?.drawImage(image, 0, 0, canvas.width, canvas.height);


                let track2d_this_lane;
                if(this.props.track2d !== undefined)
                    track2d_this_lane =  this.props.track2d.get(this.lanePositionLUT[this.state.lane]);
                let track3d_this_lane;
                if(this.props.track3d !== undefined)
                    track3d_this_lane =  this.props.track3d.get(this.lanePositionLUT[this.state.lane]);


                const track2d = track2d_this_lane?.find((track) =>
                    uuidToString(track.context_image_id) === uuidToString(imageRef?.id));
                const track3d = track3d_this_lane?.find((track) =>
                     track.context_image_id.map((ident) => uuidToString(ident)).includes(uuidToString(imageRef?.id)));

                if (this.state.selectedTrack !== undefined){
                    Object.keys(this.state.selectedTrack).forEach((selectedTrack:string) => {
                        const selTrack3d = track3d?.tracks.find((tr) => uuidToString(tr.id) === selectedTrack)
                        const selTrack2dID = selTrack3d?.assignments[0].track2D;
                        const selTrack2d = track2d?.tracks.find((tr) => uuidToString(tr.id) === uuidToString(selTrack2dID))

                        if (selTrack2d) {
                            let rect = selTrack2d.current_position;
                            if((this.props.showContextAttribution !== undefined && this.props.showContextAttribution === true ) ||
                               (this.props.showContextAttribution === undefined && this.props.multipleCamera)){
                                const label = this.state.selectedTrack![selectedTrack]
                                this.drawBoundingBox(context, rect, canvas.width / image.width, label!.toString())
                            }
                        }
                    });
                }
            }

            this.replayQueryActive = false;
            if (this.currentReplayImage !== this.nextReplayImage) {
                this.drawReplayFrame(this.nextReplayImage);
            }
        }).catch((reason) => {
            setTimeout(() => {
                this.replayQueryActive = false;
                this.drawReplayFrame(this.nextReplayImage);
            }, 500);
        })
    }

    clearFrame(){
        const canvas = this.canvasRef.current;
        const container = this.canvasContainerRef.current;
        if(canvas === null || container === null)
            return
        if (canvas.width !== container.clientWidth)
            canvas.width = container.clientWidth;
        const context = canvas.getContext('2d');
        if (context === null)
            return;
        context.fillStyle = 'rgb(16,16,16)';
        context.globalAlpha = 1;
        context.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    }

    resize(){
        if (this.canvasRef.current !== null){
            this.canvasRef.current.width = 0;
            this.canvasRef.current.height = 0;
        }

        this.drawLoadingFrame();
        if (this.props.liveStream){
            this.drawLiveFrame();
        } else {
            let images;
            if (this.props.images !== undefined)
                images = this.props.images.get(this.lanePositionLUT[this.state.lane]);
            const image = this.nearestImage(images, this.props.replayTime)
            this.drawReplayFrame(image);
            this.checkDefaultTrack();
        }
    }

    cleanupBeforeShutdown(){
        if (this.peerConnection !== null) {
            this.shutdown = true;
            this.handleClose();
            this.peerConnection = null;
        }
    }

    async connectVideo(use_turn: boolean) {
        //console.log('ContextDisplay ConnectVideo start')
        let config: RTCConfiguration = {
            iceServers: []
        };

        if (use_turn) {
            config!.iceServers!.push({
                urls: 'turn:purdas.psicorp.com:8400?transport=udp',
                username: 'psicorp',
                credential: 'v2yFqAuu933W'
            })
        }
        config!.iceServers!.push({
            urls: 'turn:'+window.location.hostname+':8400?transport=udp',
            username: 'owl',
            credential: 'password'
        })
        this.peerConnection = new RTCPeerConnection(config);
        this.peerConnection.addTransceiver('video', { direction: 'recvonly' });

        const ontrack = (e: RTCTrackEvent) => {
            //console.log('ContextDisplay ConnectVideo ontrack')
            this.video.srcObject = e.streams[0];
            let playPromise = this.video.play();
            if(playPromise !== undefined){
                playPromise.then(_ => {
                    //console.log('ContextDisplay after play promise then')
                })
                .catch(error =>{
                    console.log(error)
                })
            }

            setTimeout(() => {
                if (this.animationFrame !== null)
                    window.cancelAnimationFrame(this.animationFrame);
                this.drawLiveFrame();
            }, 500)
        }
        this.peerConnection.addEventListener('track', ontrack);

        const ongatheringstatechange = async () => {
            if (this.peerConnection === null)
                return;

            if (this.peerConnection.iceGatheringState === 'complete') {
                if (this.peerConnection.localDescription === null)
                    this.handleClose();

                try {
                    const response = this.props.remoteDevice ?
                        await this.remoteConnect(this.peerConnection) :
                        await this.localConnect(this.peerConnection);
                    //console.log('ContextDisplay response from this.peerConnection')
                    const answer = await response.json();
                    //console.log(answer);
                    try {
                        await this.peerConnection.setRemoteDescription(answer);
                    } catch (error) {
                        this.handleClose();
                        console.error(`Failed to set session description: ${error.toString()}`);
                    }
                } catch (error) {
                    console.error(`Failed to establish connection: ${error.toString()}`);
                    this.handleClose();
                }
            }
        }
        this.peerConnection.addEventListener('icegatheringstatechange', ongatheringstatechange);

        const onconnectionstatechange = async () => {
            if (this.peerConnection === null)
                return;

            switch (this.peerConnection.connectionState) {
                case "connecting":
                    //console.log('peerConnection state connecting')
                    this.restartIceFlag = false;
                    break
                case "connected":
                    //console.log('peerConnection state CONNECTED')
                    this.setState({ connected: true });
                    this.restartIceFlag = false;
                    break;
                case "disconnected":
                    //console.log('peerConnection state disconnected')
                    this.restartIceFlag = true;
                    this.handleClose();
                case "failed":
                    //console.log('peerConnection state failed')
                    this.restartIceFlag = true;
                    this.handleClose();
                case "closed":
                    //console.log('peerConnection state closed')
                    this.restartIceFlag = true;
                    this.handleClose();
                    break;
            }
        }
        this.peerConnection.addEventListener('connectionstatechange', onconnectionstatechange);

        let offerOptions = {iceRestart: false};
        if (this.restartIceFlag)
            offerOptions.iceRestart = true;
        const offer = await this.peerConnection.createOffer(offerOptions);
        try {
            await this.peerConnection.setLocalDescription(offer);
        } catch (error) {
            console.error(`Failed to set local description: ${error.toString()}`);
            this.handleClose();
        }
        //await this.peerConnection.setLocalDescription(offer);
    }

    nearestImage(images?: messages.Contextual.ContextImageReference[], time?: Date) {
        if (images === undefined || time === undefined || images.length === 0)
            return undefined;

        // Select visible images
        let sensorImages = images.filter((image) => uuidToString(image.sensor) === '69711f50-d822-5905-acb1-6da79da20836');
        if (sensorImages.length === 0) return undefined;

        const timestamp = time.getTime();
        const selected = sensorImages.reduce((closest, current) => {
            return Math.abs(timestamp - current.time) < Math.abs(timestamp - closest.time)? current: closest
        })

        return selected;
    }

    checkDefaultTrack() {
        if (this.state.selectedTrack !== undefined && this.props.attributionAnalysis !== undefined) {
            let clear_selected = true;
            const attrs_map = this.props.attributionAnalysis.attribution.map((attr) => uuidToString(attr.track3D));
            Object.keys(this.state.selectedTrack).forEach(selectedTrack => {
                if(attrs_map.includes(selectedTrack)){
                    clear_selected = false;
                }
            });
            if(clear_selected){
                this.setState( {selectedTrack: undefined} );
            }

        }
        if (this.state.selectedTrack === undefined && this.props.attributionAnalysis !== undefined) {
            const attrs = this.props.attributionAnalysis.attribution.sort(
                (a, b) => -a.likelihood + b.likelihood)
                .filter((val) => val.likelihood > -1e100);
            if (attrs.length > 0) {
                let defaultSelectedTracks:{[key:string]:number} = {};
                attrs.forEach((attr,idx) => {
                    if(idx < this.showTopNTracks){
                        defaultSelectedTracks[uuidToString(attr.track3D)] = idx+1;
                    }
                });

                this.setState( {selectedTrack: defaultSelectedTracks} )
                this.defaultTracksShown = true;
            }
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.resize);
        this.drawLoadingFrame();
        if (this.props.liveStream)
            this.connectVideo(false);
        else {
            let images;
            if (this.props.images !== undefined)
                images = this.props.images.get(this.lanePositionLUT[this.state.lane]);
            const image = this.nearestImage(images, this.props.replayTime)
            this.drawReplayFrame(image);
            this.checkDefaultTrack();
        }
        //window.addEventListener("beforeunload", this.cleanupBeforeShutdown);
    }

    componentDidUpdate(prevProps: ContextDisplayProps, prevState: ContextDisplayState) {
        let positionLUTChange = false;
        if(prevProps.lanePositionLUT && this.props.lanePositionLUT){
            if(JSON.stringify(prevProps.lanePositionLUT) !== JSON.stringify(this.props.lanePositionLUT)){
                if(this.props.lanePositionLUT !== undefined){
                    this.lanePositionLUT = this.props.lanePositionLUT;
                }
                positionLUTChange = true;
            }
        } else if (prevProps.lanePositionLUT === undefined && this.props.lanePositionLUT !== undefined){
            if(this.props.lanePositionLUT !== undefined){
                this.lanePositionLUT = this.props.lanePositionLUT;
            }
            positionLUTChange = true;
        }

        let cameraChange = prevState.thermalCamera !== this.state.thermalCamera;
        let viewChange = prevState.lane !== this.state.lane;

        if(this.props.initLane !== undefined &&  this.state.lane !== this.props.initLane){
            this.setState({ lane:this.props.initLane});
            viewChange = true;
        }

        let images_this_lane;
        if (this.props.images !== undefined)
            images_this_lane = this.props.images.get(this.lanePositionLUT[this.state.lane]);
        let prev_images_this_lane;
            if (prevProps.images !== undefined)
                prev_images_this_lane = prevProps.images.get(this.lanePositionLUT[this.state.lane]);

        const curImage = this.nearestImage(prev_images_this_lane, prevProps.replayTime);
        const nextImage = this.nearestImage(images_this_lane, this.props.replayTime);
        let imageChange = curImage !== nextImage;
        let selectedChange = JSON.stringify(prevState.selectedTrack) !== JSON.stringify(this.state.selectedTrack)
        let invChange = prevProps.replayInvestigation !== this.props.replayInvestigation;
        let attrChange = prevProps.showContextAttribution !== this.props.showContextAttribution



        if (this.props.liveStream && (!prevProps.liveStream || cameraChange || viewChange || positionLUTChange)) {
            //Clear tracks when returning to liveview
            this.setState({selectedTrack:undefined});
            this.defaultTracksShown = false;

            // Force context video connection to close and reset
            this.handleClose();

        } else if (!this.props.liveStream && (prevProps.liveStream || imageChange || selectedChange || viewChange || invChange || attrChange)) {
            if (this.state.connected)
                this.handleClose();

            //this.drawLoadingFrame();
            if (invChange){
                this.clearFrame();
                this.defaultTracksShown = false;
            }

            this.drawReplayFrame(nextImage);
        }

        if(!this.defaultTracksShown && !this.props.liveStream){
            this.checkDefaultTrack();
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.resize);
        this.cleanupBeforeShutdown();
        //window.removeEventListener("beforeunload", this.cleanupBeforeShutdown); //Remove because it interferes with download and is not necessary
    }

    onClickLaneChange(){
        if(this.props.initLane === undefined || (this.props.showLaneSelector !== undefined && this.props.showLaneSelector)){
            const new_lane = (this.state.lane+1)%2;
            this.setState({ lane: new_lane});

            //TODO bubble up to App of this change
            if(this.props.contextDisplayInterface !== undefined)
                this.props.contextDisplayInterface?.updateLane(new_lane);
        }

    }

    doesNeutronDetMatchPosition(){
        let result:boolean = true; //Default is to show live detection indicator
        if(this.props.config !== undefined){
            if(this.props.config.agg !== undefined){
                if(this.props.config?.agg?.system_configuration?.configuration_type === messages.ConfigurationState.SystemConfigurationType.OWL_PERM_MOBILE){
                    //System has two OWLS so neutron direction can be ambiguous if there's only one LANCER
                    result = false;
                    if(this.props.config?.agg?.detector_layout !== undefined){
                        this.props.config?.agg?.detector_layout?.forEach(this_detector_position => {
                            if(this_detector_position.detector_type === messages.ConfigurationState.DetectorType.LANCER){
                                if((this.state.lane === 0 && this_detector_position.position_description === 'left') ||
                                   (this.state.lane === 1 && this_detector_position.position_description === 'right')){
                                    result = true;
                                }
                            }
                        })
                    }
                }
            }
        }
        return result;
    }

    isAlarmingNow(){
        let result = false;
        if(this.props.investigations !== undefined && this.props.liveStream){
            if(this.props.investigations.length > 0){
                let inv = this.props.investigations[0]; //Time sorted so latest investigation is index 0
                if('type' in inv){
                    //Encounter message
                    if(inv.type === messages.Encounter.EncounterType.RAD){
                        if(!inv.finalized && inv.isotopes.length > 0){
                            if(this.props.multipleCamera || this.turnBorderRedOnLiveDetection){
                                if(inv.source_lane === this.lanePositionLUT[this.state.lane]){
                                    //MULTI OWL VIEW, MATCH LANE
                                    if(this.doesNeutronDetMatchPosition() || inv.isotopes.length !== 1 || inv.isotopes[0].name !== 'NEUTRONS'){
                                        result = true;
                                    }
                                }
                            }
                        }
                    }
                } else {
                    //Investigation message (RAD ONLY)
                    if(!inv.finalized && inv.isotopes.length > 0){
                        if(this.props.multipleCamera || this.turnBorderRedOnLiveDetection){
                            if(inv.source_lane === this.lanePositionLUT[this.state.lane]){
                                //MULTI OWL VIEW, MATCH LANE
                                if(this.doesNeutronDetMatchPosition() || inv.isotopes.length !== 1 || inv.isotopes[0].name !== 'NEUTRONS'){
                                    result = true;
                                }
                            }
                        }
                    }
                }
            }
        }
        return(result)
    }

    getAlarmingName(){
        let result = '';
        if(this.props.investigations !== undefined && this.props.liveStream){
            if(this.props.investigations.length > 0){
                let inv = this.props.investigations[0]; //Time sorted so latest investigation is index 0
                if('type' in inv){
                    //Encounter message
                    if(inv.type === messages.Encounter.EncounterType.RAD){
                        if(!inv.finalized && inv.isotopes.length > 0){
                            if(this.props.multipleCamera || this.turnBorderRedOnLiveDetection){
                                if(inv.source_lane === this.lanePositionLUT[this.state.lane]){
                                    //MULTI OWL VIEW, MATCH LANE
                                    result = inv.isotopes[0].name;
                                }
                            }
                        }
                    }
                } else {
                    //Investigation message (RAD ONLY)
                    if(!inv.finalized && inv.isotopes.length > 0){
                        if(this.props.multipleCamera || this.turnBorderRedOnLiveDetection){
                            if(inv.source_lane === this.lanePositionLUT[this.state.lane]){
                                //MULTI OWL VIEW, MATCH LANE
                                result = inv.isotopes[0].name;
                            }
                        }
                    }
                }
            }
        }
        return(result)
    }
    getAlarmingCategory(){
        let result = '';
        if(this.props.investigations !== undefined && this.props.liveStream){
            if(this.props.investigations.length > 0){
                let inv = this.props.investigations[0]; //Time sorted so latest investigation is index 0
                if('type' in inv){
                    //Encounter message
                    if(inv.type === messages.Encounter.EncounterType.RAD){
                        if(!inv.finalized && inv.isotopes.length > 0){
                            if(this.props.multipleCamera || this.turnBorderRedOnLiveDetection){
                                if(inv.source_lane === this.lanePositionLUT[this.state.lane]){
                                    //MULTI OWL VIEW, MATCH LANE
                                    result = inv.isotopes[0].category;
                                }
                            }
                        }
                    }
                } else {
                    //Investigation message (RAD ONLY)
                    if(!inv.finalized && inv.isotopes.length > 0){
                        if(this.props.multipleCamera || this.turnBorderRedOnLiveDetection){
                            if(inv.source_lane === this.lanePositionLUT[this.state.lane]){
                                //MULTI OWL VIEW, MATCH LANE
                                result = inv.isotopes[0].category;
                            }
                        }
                    }
                }
            }
        }
        return(result)
    }
    hyphenated_nuclide_name(underscore_name: string) {
        var output = underscore_name;
        if (underscore_name.includes("_")) {
            var res = underscore_name.split("_");
            var nuclide_symbol = res[0].toLowerCase();
            var nuclide_number = res[1];
            output = nuclide_symbol[0].toUpperCase() + nuclide_symbol.slice(1) +
                "-" + nuclide_number.toLowerCase();
        }
        return output;
    }

    get_isotope_color(isot_class: string) {
        var this_display_category = isot_class.toUpperCase();
        var color = 'olivedrab'; // NORM

        // COLOR DEFINITION OF
        if (this_display_category.match('INDUSTRIAL')) {
            color = 'orange';
        }
        if (this_display_category.match('RDD')) {
            color = 'orange';
        }
        if (this_display_category.match('MEDICAL')) {
            color = '#4f86f7';
        }
        if (this_display_category.match('SNM')) {
            color = 'red';
        }
        if (this_display_category.match('NUCLEAR')) {
            color = 'red';
        }
        if (this_display_category.match('OVERRANGE')) {
            color = 'OrangeRed';
        }
        if (this_display_category.match('HIGH_DOSE')) {
            color = 'OrangeRed';
        }
        return color;
    }

    get_class_icon(isot_class: string){
        const this_display_category = isot_class.toUpperCase();
        let icon = icon_attention_logo; // Attention
        if (this_display_category === 'INDUSTRIAL') {
            icon = icon_industrial_logo;
        }
        if (this_display_category === 'RDD') {
            icon = icon_industrial_logo;
        }
        // if (this_display_category == 'MEDICAL') {
        if (this_display_category === 'MEDICAL') {
            icon = icon_medical_logo;
        }
        if (this_display_category === 'SNM') {
            icon = icon_nuclear_logo;
        }
        if (this_display_category === 'NUCLEAR') {
            icon = icon_nuclear_logo;
        }
        if (this_display_category === 'OVERRANGE') {
            icon = icon_attention_logo;
        }
        if (this_display_category === 'HIGH_DOSE') {
            icon = icon_attention_logo;
        }
        if (this_display_category === 'NORM') {
            icon = icon_norm_logo;
        }
        return icon;
    }

    onSelectTrack(thisTrack:string, thisRank:number){
        let selectedTracks:{[key:string]:number} = {}
        if (this.state.selectedTrack !== undefined){
            //Copy so that it will get differentiated by ComponentDidUpdate
            selectedTracks = JSON.parse(JSON.stringify(this.state.selectedTrack)); //Deep Clone hack
            if(Object.keys(this.state.selectedTrack).includes(thisTrack)){
                //Remove selected
                delete selectedTracks[thisTrack]
                //console.log('Removing '+thisTrack)
            } else {
                //Add selected
                selectedTracks[thisTrack] = thisRank;
                //console.log('Adding '+thisTrack)
            }
            this.setState( {selectedTrack: selectedTracks} )
        } else {
            //This is the first on the selectedTrack list
            selectedTracks[thisTrack] = thisRank;
            this.setState( {selectedTrack: selectedTracks} )
        }
        this.setState( {selectedTrack: selectedTracks} )
    }

    onSelectSource(thisSource:string, timestamp:number){
        let selectedSources:{[key:string]:number} = {}
        if (this.props.coralInterface?.selectedSource !== undefined){
            if(Object.keys(this.props.coralInterface?.selectedSource).includes(thisSource)){
                //Remove selected
                delete selectedSources[thisSource]
            } else {
                //Add selected
                selectedSources[thisSource] = timestamp;
                //Select this investigation
                if(this.props.coralInterface?.replayInvestigation !== undefined)
                    this.props.coralInterface?.updateSelectedInvestigation(this.props.coralInterface.replayInvestigation)
            }
        } else {
            //This is the first on the selectedTrack list
            selectedSources[thisSource] = timestamp;
            //Select this investigation
            if(this.props.coralInterface?.replayInvestigation !== undefined)
                this.props.coralInterface?.updateSelectedInvestigation(this.props.coralInterface.replayInvestigation)
        }
        this.props.coralInterface?.updateSelectedSource(selectedSources)

    }

    render() {
        let laneSelector;
        if(this.props.multipleCamera && (this.props.initLane===undefined || (this.props.showLaneSelector !== undefined && this.props.showLaneSelector))){
            laneSelector =
                <div style={{ display: 'flex', position: 'relative', justifyContent: 'flex-start' }}>
                    <div style={{ display: 'flex', position: 'absolute', left: '0px', bottom: '0px', padding: '5px' }}>
                        <div className="round-button" style={{ padding: '5px', width:'48px', height:'48px'}}
                                onClick={() => this.onClickLaneChange()}>
                            <div style={{display: 'flex', flexDirection: 'column', paddingTop: '2px', paddingBottom: '2px' }}>
                                <img src={icon_switch_video_logo} alt="Switch Video View" />
                            </div>
                        </div>
                    </div>
                </div>
        }

        let imageAnalyses_this_lane;
        if(this.props.imageAnalyses !== undefined)
            imageAnalyses_this_lane = this.props.imageAnalyses.get(this.lanePositionLUT[this.state.lane]);
        let track2d_this_lane;
        if(this.props.track2d !== undefined)
            track2d_this_lane =  this.props.track2d.get(this.lanePositionLUT[this.state.lane]);
        let track3d_this_lane;
        if(this.props.track3d !== undefined)
            track3d_this_lane =  this.props.track3d.get(this.lanePositionLUT[this.state.lane]);

        let inv_class;
        let inv_icon;
        let inv_isot;
        let inv_color;
        let rect_color = 'red'; //DEFAULT is RED
        let neutron_det = false;

        const name = this.getAlarmingName();

        if(name === "OVERRANGE"){
            inv_class = "ID UNCERTAIN";
            inv_icon = this.get_class_icon("OVERRANGE");
            inv_isot = "OVERRANGE";
            inv_color = this.get_isotope_color("OVERRANGE");
        } else if(name === "HIGH_DOSE"){
            inv_class = "CAUTION";
            inv_icon = this.get_class_icon("HIGH_DOSE");
            inv_isot = "HIGH DOSE";
            inv_color = this.get_isotope_color("HIGH_DOSE");
        } else {
            inv_class = this.getAlarmingCategory();
            inv_icon = this.get_class_icon(inv_class);
            inv_isot = this.hyphenated_nuclide_name(name);
            inv_color = this.get_isotope_color(inv_class);
            if(name === "NEUTRONS"){
                rect_color = 'blue';
                neutron_det = true;
            }
        }
        let liveLaneLabel;
        if(this.isAlarmingNow()){
            liveLaneLabel =
                <div className="detection-metric-container" style={{padding: '0px', alignItems:'start', flex: 'none'}}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignSelf: 'center'}}>
                        <div className='lane-threat-class-image'>
                            <img src={inv_icon} style={{color: inv_color}}alt={inv_class} />
                        </div>
                        <div style={{ display: 'flex', flex: '1', flexDirection: 'column'}}>
                            <label className={"lane-threat-isotope metric"}><span style={{color: inv_color}}>{t(inv_isot,{ns:'trans'})}</span></label>
                            <label className={"lane-threat-class metric"}><span style={{color: inv_color}}>{t(inv_class,{ns:'trans'})}</span></label>
                        </div>
                    </div>
                </div>
        }
        const liveDetectionDisplay =
            <div style={{backgroundColor:this.isAlarmingNow()?'red':'rgb(16,16,16)'}}>
                {liveLaneLabel}
            </div>

        let contextAttributionList;
        if((this.props.showContextAttribution !== undefined && this.props.showContextAttribution === true ) ||
           (this.props.showContextAttribution === undefined && this.props.multipleCamera)){
            //Display attribution result only if in case of multiple OWLs to do image attribution
            contextAttributionList = <ContextAttributionList analysis={imageAnalyses_this_lane} track2d={track2d_this_lane}
                    track3d={track3d_this_lane} attribution={this.props.attributionAnalysis}
                    selectedTrack={this.state.selectedTrack}
                    onSelectTrack={(track,thisRank) => this.onSelectTrack(track,thisRank)}
                    replayInvestigation={this.props.replayInvestigation}/>
        }
        //CORAL Result
        let isChemEncounter=false;
        let coralResultList;
        if(this.props.replayInvestigation !== undefined){
            if('type' in this.props.replayInvestigation){
                if(this.props.replayInvestigation.type === messages.Encounter.EncounterType.CHEM){
                    isChemEncounter = true;
                    coralResultList = <CoralResultList coralResults = {this.props.coralInterface?.coralResults} selectedSource={this.props.coralInterface?.selectedSource}
                                        onSelectSource={(source,timestamp) => this.onSelectSource(source,timestamp)}
                                        replayInvestigation={this.props.replayInvestigation}/>

                }
            }
        }


        let cameraSelector;
        if(this.props.hideCameraSelector === undefined || this.props.hideCameraSelector === false){
            cameraSelector =
                <div style={{ display: 'flex', position: 'relative', justifyContent: 'flex-end' }}>
                    <div style={{ display: 'flex', position: 'absolute', right: '0px', bottom: '0px', padding: '5px' }}>
                        <div className="round-button" style={{ padding: '5px' }}
                                onClick={() => this.setState({ thermalCamera: !this.state.thermalCamera })}>
                            <img src={this.state.thermalCamera ? night_logo : day_logo} alt="Thermal/Visible Toggle" />
                        </div>
                    </div>
                </div>
        }


        let coralImageDisplay;
        let coralSourceIdx;
        if(this.props.coralInterface !== undefined && this.props.coralInterface.selectedSource !== undefined){
            if (Object.keys(this.props.coralInterface.selectedSource).length > 0 && this.props.coralInterface.coralResults !== undefined &&
                this.props.replayInvestigation !== undefined && 'type' in this.props.replayInvestigation){
                    let contextImage;
                    const selectedSourceName = Object.keys(this.props.coralInterface.selectedSource)[0];
                    const coralResult = this.props.coralInterface.coralResults[uuidToString(this.props.replayInvestigation.id)];
                    if(coralResult !== undefined)
                        coralResult.sources.forEach((source,idx) => {
                            if(source.name === selectedSourceName){
                                contextImage = source.image;
                                coralSourceIdx = idx;
                            }
                        });
                        coralImageDisplay =
                            <div style={{position: "relative", width:"100%",zIndex:1001}}>
                                <div style={{position: "absolute", top:'0px',right:'0px',width:"100%"}}>
                                    <CoralImageDisplay contextImage={contextImage}/>
                                </div>
                            </div>
                }
        }

        return <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                <div className="detection-metric-container" style={{padding: '0px', alignItems:'start', flex: 'none',
                    borderBottomLeftRadius:0,borderBottomRightRadius:0, margin: '0px',
                    backgroundColor:this.isAlarmingNow()?'red':'rgb(16,16,16)'}}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignSelf: 'center'}}>
                        {
                        coralSourceIdx !== undefined ?
                        <div style={{ padding: '0px', color: 'white', fontSize: '1.5em'}}>{t('CORAL IMAGE',{ns:'trans'})}</div> :
                        (this.props.multipleCamera ?
                            (this.state.lane==0 ?  <div style={{ padding: '0px', color: 'white', fontSize: '1.5em'}}>{t('LEFT',{ns:'trans'})}</div> :
                                                   <div style={{ padding: '0px', color: 'white', fontSize: '1.5em'}}>{t('RIGHT',{ns:'trans'})}</div>) :
                                                   <div style={{ padding: '0px', color: 'white', fontSize: '1.5em'}}>{t('CAMERA',{ns:'trans'})}</div>)
                        }


                    </div>
                </div>
                <div style={{borderWidth:'3px', borderStyle:'solid', borderColor:this.isAlarmingNow()?'red':'rgb(16,16,16)'}}>
                    {coralImageDisplay}
                    <div ref={this.canvasContainerRef} style={{ width: '100%'}}>
                        <canvas id="liveVideo" ref={this.canvasRef} />
                    </div>
                </div>
                {laneSelector}
                {cameraSelector}
                {this.props.liveStream ? liveDetectionDisplay : (isChemEncounter ? coralResultList : contextAttributionList)}
        </div>;
    }
} withTranslation()

export default ContextDisplay

export function getContextLanePositionFromData(spectra:messages.Rad.RadiationReading[]){

    let replayLanePositionLUT:LanePosition = [];
    if(spectra !== undefined || spectra !== null){
        if(spectra.length > 0){
            const reading = spectra[0];
            if(reading.detectors.length > 0){
                reading.detectors.forEach((det) => {
                    if(det.spectrum !== null){
                        if(det.angular_position === 270){
                            replayLanePositionLUT[0] = det.lane_position;
                        } else if(det.angular_position === 90){
                            replayLanePositionLUT[1] = det.lane_position;
                        }
                    }
                });
            }
        }
    }
    return replayLanePositionLUT;
}
